"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.WallItemBuilder = void 0;
const domain_id_1 = require("@streem/domain-id");
const toolbox_1 = require("@streem/toolbox");
const streem_sdk_protobuf_1 = require("streem-sdk-protobuf");
let heartbeatCount = 0;
class WallItemBuilder {
    constructor(ownerDeviceId, ownerUserId) {
        this.ownerDeviceId = ownerDeviceId;
        this.ownerUserId = ownerUserId;
    }
    buildStreemshotRequestItem(requestedOfUserId, requestedOfDeviceId) {
        const id = new domain_id_1.StreemshotRequestId().toString();
        const streemshotId = new domain_id_1.StreemshotId().toString();
        return this.buildWallItem({
            id,
            type: 'streemshotRequest',
            streemshotRequest: {
                streemshotId,
                requestedOfUserId: requestedOfUserId.toString(),
                requestedOfDeviceId: requestedOfDeviceId.toString(),
            },
        });
    }
    buildStreemshotResponseItem(streemshotId, streemshotPayload) {
        return this.buildWallItem({
            id: streemshotId.toString(),
            type: 'streemshot',
            streemshot: new streem_sdk_protobuf_1.WallItem.Streemshot(streemshotPayload),
        });
    }
    buildStreemshotRevisionItem(streemshotId, streemshotPayload) {
        return this.buildWallItem({
            id: streemshotId.toString(),
            type: 'streemshotRevision',
            streemshotRevision: new streem_sdk_protobuf_1.WallItem.StreemshotRevision(streemshotPayload),
        });
    }
    buildGeolocationRequestWallItem(requestedOfUserId, requestedOfDeviceId) {
        const id = new domain_id_1.GPSRequestId().toString();
        return this.buildWallItem({
            id,
            type: 'artifactRequest',
            artifactRequest: {
                artifactId: id,
                requestedForUserId: requestedOfUserId.toString(),
                requestedForDeviceId: requestedOfDeviceId.toString(),
                artifactType: streem_sdk_protobuf_1.WallItem.ArtifactType.GPS_POSITION,
            },
        });
    }
    buildGeolocationResponseItem(gpsId, gpsPayLoad) {
        return this.buildWallItem({
            id: gpsId.toString(),
            type: 'artifact',
            artifact: new streem_sdk_protobuf_1.WallItem.Artifact(gpsPayLoad),
        });
    }
    buildBookmarkWallItem(bookmarkPayload, id) {
        const bookmarkId = id || new domain_id_1.BookmarkId().toString();
        return this.buildWallItem({
            id: bookmarkId,
            type: 'artifact',
            artifact: new streem_sdk_protobuf_1.WallItem.Artifact(bookmarkPayload),
        });
    }
    buildRoomOutcomeReportWallItem(roomOutcomeReport, id) {
        const roomOutcomeReportId = id || new domain_id_1.RoomOutcomeReportId().toString();
        return this.buildWallItem({
            id: roomOutcomeReportId,
            type: 'artifact',
            artifact: new streem_sdk_protobuf_1.WallItem.Artifact(roomOutcomeReport),
        });
    }
    buildFavoriteWallItem(userId) {
        return this.buildWallItem({
            id: `favorite:${userId}`,
            type: 'userFavorite',
            userFavorite: {
                userSid: userId.toString(),
            },
        });
    }
    buildRoomWallItem(room) {
        return this.buildWallItem({ room, id: 'room', type: 'room' });
    }
    buildUserWallItem(id, user) {
        return this.buildWallItem({ user, id: id.toString(), type: 'user' });
    }
    buildTrackWallItem(type, track) {
        (0, toolbox_1.invariant)(this.ownerUserId, 'Expected ownerUserId to be defined when building track wall item');
        return this.buildWallItem({
            [type]: track,
            id: this.getTrackId(type, this.ownerUserId),
            type,
        });
    }
    buildHeartbeatWallItem() {
        return this.buildWallItem({
            id: `heartbeat:${this.ownerUserId}`,
            type: 'heartbeat',
            heartbeat: {
                timestamp: this.getProtobufTimestamp(),
                count: ++heartbeatCount,
            },
        });
    }
    buildARScanningRequest(arScanningRequest) {
        const id = new domain_id_1.ScanningRequestId().toString();
        return this.buildWallItem({
            id,
            type: 'arScanningRequest',
            arScanningRequest,
        });
    }
    buildARAnchorRequest(arAnchorRequest) {
        const id = new domain_id_1.AnchorRequestId().toString();
        return this.buildWallItem({
            id,
            type: 'arAnchorRequest',
            arAnchorRequest,
        });
    }
    buildCatalogItem(id, catalogItem) {
        return this.buildWallItem({
            id: id.toString(),
            type: 'catalogItem',
            catalogItem,
        });
    }
    buildElementGroup(id, elementGroup) {
        return this.buildWallItem({
            id: id.toString(),
            type: 'elementGroup',
            elementGroup,
        });
    }
    buildCapabilities(capabilities) {
        // Capabilities specific to device - use deviceId to create reproducible IDs
        const id = new domain_id_1.CapabilitiesId(this.ownerDeviceId.toUUID()).toString();
        return this.buildWallItem({
            id,
            type: 'capabilities',
            capabilities,
        });
    }
    buildCameraSubjectRequestWallItem(requestedForUserId, requestedForDeviceId, requestedCameraSubject) {
        const id = new domain_id_1.CameraSubjectRequestId().toString();
        return this.buildWallItem({
            id: id,
            type: 'cameraSubjectRequest',
            cameraSubjectRequest: {
                requestedForUserId: requestedForUserId.toString(),
                requestedForDeviceId: requestedForDeviceId.toString(),
                requestedCameraSubject: requestedCameraSubject,
            },
        });
    }
    buildCameraSubjectResponseWallItem(inResponseToWallItemId, accepted, status) {
        const id = new domain_id_1.CameraSubjectResponseId().toString();
        // when status is not set, infer its value based on accepted.  Only legacy versions of
        //    the mobile apps will omit status and they do not implement abort.
        const cameraResponseStatus = status
            ? status
            : accepted
                ? streem_sdk_protobuf_1.CameraSubjectResponseStatus.CAMERA_SUBJECT_RESPONSE_STATUS_ACCEPTED
                : streem_sdk_protobuf_1.CameraSubjectResponseStatus.CAMERA_SUBJECT_RESPONSE_STATUS_REJECTED;
        return this.buildWallItem({
            id: id,
            type: 'cameraSubjectResponse',
            cameraSubjectResponse: {
                accepted: accepted,
                inResponseToWallItemId: inResponseToWallItemId,
                status: cameraResponseStatus,
            },
        });
    }
    buildCreateDetectionWallItem(streemshotId, description, type) {
        var _a;
        const id = new domain_id_1.FeedbackId().toString();
        return this.buildWallItem({
            id,
            type: 'userDetectionFeedback',
            userDetectionFeedback: {
                streemshotId: streemshotId.toString(),
                submittedByUserId: (_a = this.ownerUserId) === null || _a === void 0 ? void 0 : _a.toString(),
                createDetection: {
                    description,
                    type,
                },
            },
        });
    }
    buildUpdateDetectionWallItem(streemshotId, detectionId, description) {
        var _a;
        const id = new domain_id_1.FeedbackId().toString();
        return this.buildWallItem({
            id,
            type: 'userDetectionFeedback',
            userDetectionFeedback: {
                streemshotId: streemshotId.toString(),
                submittedByUserId: (_a = this.ownerUserId) === null || _a === void 0 ? void 0 : _a.toString(),
                updateDetection: {
                    detectionId: detectionId.toString(),
                    description,
                },
            },
        });
    }
    buildHideDetectionWallItem(streemshotId, detectionId) {
        var _a;
        const id = new domain_id_1.FeedbackId().toString();
        return this.buildWallItem({
            id,
            type: 'userDetectionFeedback',
            userDetectionFeedback: {
                streemshotId: streemshotId.toString(),
                submittedByUserId: (_a = this.ownerUserId) === null || _a === void 0 ? void 0 : _a.toString(),
                hideDetection: {
                    detectionId: detectionId.toString(),
                },
            },
        });
    }
    buildWallItem(itemData) {
        (0, toolbox_1.invariant)(this.ownerUserId, 'Expected ownerUserId to be defined when building wall item');
        return new streem_sdk_protobuf_1.WallItem(Object.assign(Object.assign({}, itemData), { ownerUserId: this.ownerUserId.toString(), ownerDeviceId: this.ownerDeviceId.toString(), clientCreatedAt: this.getProtobufTimestamp() }));
    }
    getProtobufTimestamp(timestamp) {
        let date;
        if (!timestamp) {
            date = new Date();
        }
        else if (typeof date === 'string') {
            date = new Date(timestamp);
        }
        else {
            date = timestamp;
        }
        const millis = date.getTime();
        return new streem_sdk_protobuf_1.Timestamp({
            seconds: new streem_sdk_protobuf_1.Long(millis / 1000),
            nanos: (millis % 1000) * 1e6,
        });
    }
    getTrackId(type, ownerUserId) {
        return `${type}${ownerUserId}`;
    }
}
exports.WallItemBuilder = WallItemBuilder;
//# sourceMappingURL=wall_item_builder.js.map